import React, { useState, useEffect } from "react";
import { MobileCameraCapture } from "./take-picture";
import { RecordAudio } from "./record-audio";
import { ActionButtons } from "../atoms/action-buttons";
import { useSubmission } from "../../providers/submission-provider";
import { useTasks } from "../../providers/task-provider";

interface MediaCaptureProps {
  onMediaTaken: (audioFile: File) => void;
  onGoBack: () => void;
  audioName: string;
  content: string;
}

export const MediaCapture: React.FC<MediaCaptureProps> = ({
  audioName,
  onGoBack,
  onMediaTaken,
  content
}) => {
  const [timestamp, setTimestamp] = useState<number | null>(null);
  const [isAudioAllowed, setIsAudioAllowed] = useState(false);
  const [isAudioRecording, setIsAudioRecording] = useState(false);
  const [checkInterval, setCheckInterval] = useState<any | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const { submission } = useSubmission();
  const { setCurrentTask, currentTask } = useTasks();

  useEffect(() => {
    if (timestamp) {
      const interval = setInterval(() => {
        const timeElapsed = Date.now() - timestamp;
        if (timeElapsed > 30000) {
          if (!isAudioRecording) {
            setIsAudioAllowed(false);
            setAudioFile(null);
          }
          clearInterval(interval);
        }
      }, 1000);
      setCheckInterval(interval);
      return () => clearInterval(interval);
    }
  }, [timestamp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (audioFile) {
      onMediaTaken(audioFile);
    }
  }, [audioFile, onMediaTaken]);

  useEffect(() => {
    if (checkInterval && isAudioRecording) {
      clearInterval(checkInterval);
    }
  }, [checkInterval, isAudioRecording]);

  const handleAudioFinished = (audioFile: File) => {
    console.log("Audio file received:", audioFile);
    setAudioFile(audioFile);
  };

  return (
    <div className="media-wrapper">
      <h2 className="text-left mb-2">
        Record: {audioName}
        {(currentTask?.subcategories?.length || 0) > 1
          ? `- ${submission?.submission_metadata["sub_category"]}`
          : ""}
      </h2>
      {isAudioAllowed ?
        <div className="rounded-xl border-dark-gray-1 border text-left p-4 mb-2">
          <h3 className="font-medium mb-2">Text to read:</h3>
          <p style={{ whiteSpace: 'pre-line' }}>{content}</p>
        </div>
        : <div className="rounded-xl border-dark-gray-1 border text-left p-4 mb-2">
        <h3 className="font-medium mb-2">Instructions:</h3>
        <ol className="text-sm list-decimal list-inside">
          <li>Record audio with in 30 seconds after select "Go to Recording".</li>
          <li>Ensure minimal background noise.</li>
          <li>Microphone access should be enable for the current browser.</li>
          <li>Location services should be enable for the current browser.</li>
          <li>Record audio naturally.</li>
          <li>Minimum 3 seconds duration.</li>
          <li>Record in natural environment.</li>
        </ol>
      </div>}
      {isAudioAllowed ? (
        <>
          <RecordAudio
            onRecordingFinished={handleAudioFinished}
            onRecordingStart={() => setIsAudioRecording(true)}
          />
          <div className="mt-6">
            <ActionButtons
              primaryText=""
              secondaryText="Go Back"
              onSecondaryClick={() => {
                setCurrentTask(null);
                onGoBack();
              }}
              onPrimaryClick={() => null}
            />
          </div>
        </>
      ) : (
        <div className="mt-6 max-w-[20rem]" style={{ marginLeft: "auto", marginRight: "auto" }}>
          <ActionButtons primaryText="Go to Recording" secondaryText="Go Back" onPrimaryClick={() => {
            setTimestamp(Date.now());
            setIsAudioAllowed(true)
          }} onSecondaryClick={() => {
            setCurrentTask(null);
            onGoBack();
          }} />
        </div>
      )}
    </div>
  );
};
